import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useMemo, useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import { Dialog, DialogTitle } from '@mui/material';
// routes
import { useSnackbar } from 'src/components/snackbar';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
// _mock
// components
import Iconify from 'src/components/iconify';
import FormProvider, {
    RHFTextField,
} from 'src/components/hook-form';
import RHFDatepicker from 'src/components/hook-form/rhf-date-picker';
import { useDeletePaymentHistoryMutation, useGetPaymentByIDMutation, useUpdatePaymentMutation } from 'src/services/payment/payment-api';
import { useBoolean } from 'src/hooks/use-boolean';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { formatDate } from 'src/utils/format-time';
import { LoadingScreen } from 'src/components/loading-screen';
import ErrorView from '../error/error-view';

// ----------------------------------------------------------------------


export default function PaymentViewForm({ tourId, open, onClose }) {

    const mdUp = useResponsive('up', 'md');

    const confirm = useBoolean();

    const [paymentId, setPaymentId] = useState(null);

    const { enqueueSnackbar } = useSnackbar();

    const NewProductSchema = Yup.object().shape({
        paymentDate: Yup.date()
            .required("Payment date can't be empty")
            .typeError("Please select a valid date"),
        paymentAmount: Yup.number()
            .typeError('Payment amount must be a number')
            .required('Payment amount is required')
            .moreThan(0, 'Payment amount should be greater than 0')
    });

    const [getPaymentDetails, { isLoading: isLoadingGetPaymentDetails, isSuccess: isSuccessGetPaymentDetails, data: dataGetPaymentDetails, error: errorGetPaymentDetails, isError: isErrorGetPaymentDetails }] = useGetPaymentByIDMutation();

    const [deletePaymentHistory, { error: errorDeletePaymentHistory }] = useDeletePaymentHistoryMutation();

    const [updatePayment, { isSuccess: isSuccessUpdatePayment, error: errorUpdatePayment }] = useUpdatePaymentMutation();

    useEffect(() => {
        if(tourId){
            getPaymentDetails(tourId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tourId]);

    const defaultValues = useMemo(
        () => ({
            inqueryRef: '',
            tripCharge: '',
            driverCommision: '',
            commisionBalance: 0,
            paymentDate: null,
            paymentAmount: '',
            paymentHistory: []
        }),
        []
    );

    const methods = useForm({
        resolver: yupResolver(NewProductSchema),
        defaultValues,
    });

    function getcommisionBalance(totalAmount, paymentHistoryList) {
        let balanceAmount = totalAmount;

        for (let i = 0; i < paymentHistoryList?.length; i += 1) {
            const paid = paymentHistoryList[i].amount;
            balanceAmount -= paid;
        }
        return balanceAmount;
    }

    const {
        control,
        watch,
        reset,
        handleSubmit,
        setError,
        setValue,
        formState: { isSubmitting, },
    } = methods;

    const values = watch();

    useEffect(() => {
        if(isSuccessGetPaymentDetails){
            console.log("Payment details");
            console.log(dataGetPaymentDetails);

            setValue("inqueryRef", dataGetPaymentDetails?.inquiry_ref);
            setValue("tripCharge", dataGetPaymentDetails?.trip_charge);
            setValue("driverCommision", dataGetPaymentDetails?.driver_commission);
            setValue("commisionBalance", getcommisionBalance(dataGetPaymentDetails?.driver_commission, dataGetPaymentDetails?.payment_history));
            setValue("paymentHistory", dataGetPaymentDetails?.payment_history?.length
                ? dataGetPaymentDetails.payment_history.map(item => ({
                    ...item,
                    payment_date: new Date(item.payment_date)
                }))
                : []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccessGetPaymentDetails, dataGetPaymentDetails]);

    const { fields } = useFieldArray({
        control,
        name: 'paymentHistory',
    });

    const onSubmit = handleSubmit(async (data) => {
        try {
            if (data.paymentAmount > data.commisionBalance) {
                setError('paymentAmount', {
                    type: 'manual',
                    message: `Payment amount cannot be greater than the balance amount of $${data.commisionBalance}`,
                });
                return;
            }

            const body = {
                'amount': data.paymentAmount,
                'payment_date': formatDate(data.paymentDate)
            }

            console.log(body);
            console.log(dataGetPaymentDetails.tour_id);

            await updatePayment({ id: dataGetPaymentDetails.tour_id, body });

        } catch (error) {
            console.error(error);
        }
    });

    const handleRemove = (id) => {
        deletePaymentHistory({ tourId: dataGetPaymentDetails.tour_id, paymentID: id })
    };

    useEffect(() => {
        if (errorDeletePaymentHistory) {
            if (errorDeletePaymentHistory?.data === 'Success') {
                enqueueSnackbar('Successfully removed the payment history');
                getPaymentDetails(tourId);
            }
            else {
                enqueueSnackbar('Failed to remove the payment history', { variant: 'error' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enqueueSnackbar, errorDeletePaymentHistory])

    useEffect(() => {
        if (isSuccessUpdatePayment) {
            enqueueSnackbar('Successfully updated payment!');
            reset();
            onClose();
        }

        if (errorUpdatePayment) {
            enqueueSnackbar(errorUpdatePayment?.data?.message, { variant: 'error' });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enqueueSnackbar, errorUpdatePayment, isSuccessUpdatePayment])

    const renderTrip = (
        <>
            {mdUp && (
                <Grid md={4}>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                        Trip
                    </Typography>
                </Grid>
            )}

            <Grid xs={12} md={8}>
                <Card>
                    {!mdUp && <CardHeader title="Trip" />}

                    <Stack spacing={3} sx={{ p: 3 }}>
                        <RHFTextField disabled name="inqueryRef" label="Inquiry ID" />
                        <RHFTextField disabled name="tripCharge" label="Tour Charge" />
                        <RHFTextField disabled name="driverCommision" label="Total Commision" />
                        <RHFTextField disabled name="commisionBalance" label="Commision Balance" />
                    </Stack>
                </Card>
            </Grid>
        </>
    );

    const renderPaymentHistory = (
        <>
            {mdUp && (
                <Grid md={4}>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                        PaymentHistory
                    </Typography>
                </Grid>
            )}
            <Grid xs={12} md={8}>
                <Card>
                    {!mdUp && <CardHeader title="PaymentHistory" />}
                    {fields.length > 0 ?
                        <Stack divider={<Divider flexItem sx={{ borderStyle: 'dashed' }} />} spacing={2}>
                            {fields.map((item, index) => (
                                <Stack key={item.id} spacing={1.5} sx={{ p: 2.5 }}>
                                    <RHFDatepicker name={`paymentHistory[${index}].payment_date`} label="Date of Payment" disabled />
                                    <RHFTextField disabled name={`paymentHistory[${index}].amount`} label="Paid Commision" />
                                    <Button
                                        size="small"
                                        color="error"
                                        startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                                        sx={{ flex: 1 }}
                                        onClick={() => {
                                            setPaymentId(values.paymentHistory[index].id);
                                            confirm.onTrue()
                                        }}
                                    >
                                        Remove
                                    </Button>

                                </Stack>
                            ))}
                        </Stack>
                        : (<Typography sx={{ padding: 10, textAlign: 'center' }}>No payments made earlier</Typography>)}
                </Card>
            </Grid>

        </>
    );

    const renderMakePayment = (
        <>
            {mdUp && (
                <Grid md={4}>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                        Make Payment
                    </Typography>
                </Grid>
            )}

            <Grid xs={12} md={8}>
                <Card>
                    {!mdUp && <CardHeader title="Make Payment" />}

                    <Stack spacing={3} sx={{ p: 3 }}>
                        <RHFDatepicker name="paymentDate" label="Payment Date" disabled={getcommisionBalance(dataGetPaymentDetails?.driver_commission, dataGetPaymentDetails?.payment_history) === 0}/>
                        <RHFTextField name="paymentAmount" label="Payment Amount" disabled={getcommisionBalance(dataGetPaymentDetails?.driver_commission, dataGetPaymentDetails?.payment_history) === 0}/>
                    </Stack>
                </Card>
            </Grid>
        </>
    );

    const renderActions = (
        <>
            {mdUp && <Grid md={4} />}
            <Grid xs={12} md={8} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Box
                    sx={{ flexGrow: 1, pl: 3 }}
                />

                <LoadingButton type="submit" variant="contained" size="large" loading={isSubmitting}>
                    Update
                </LoadingButton>
            </Grid>
        </>
    );

    let body;
    
    if(isLoadingGetPaymentDetails){
        body = <LoadingScreen />
    }
    else if(isErrorGetPaymentDetails){
        body = <ErrorView error={errorGetPaymentDetails?.data}/>
    }
    else if(isSuccessGetPaymentDetails){
        body = <>
            {renderTrip}
            {renderPaymentHistory}
            {renderMakePayment}
            {renderActions}
        </>
    }

    return (
        <Dialog
            fullWidth
            maxWidth={false}
            open={open}
            onClose={() => {
                reset();
                onClose();
            }}
            PaperProps={{
                sx: { maxWidth: 900 },
            }}
        >
            <DialogTitle>
                <Typography variant="h6" gutterBottom>
                    Payment
                </Typography>
            </DialogTitle>

            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Grid container spacing={3} sx={{px: 3}}>
                    {body}
                </Grid>

                <ConfirmDialog
                    open={confirm.value}
                    onClose={() => {
                        setPaymentId(null);
                        confirm.onFalse()
                    }}
                    title="Remove"
                    content="Are you sure to remove the payment history?"
                    action={
                        <Button variant="contained" color="error" onClick={() => {
                            handleRemove(paymentId);
                            confirm.onFalse();
                        }}>
                            Delete
                        </Button>
                    }
                />
            </FormProvider>
        </Dialog>
    );
}

PaymentViewForm.propTypes = {
    tourId: PropTypes.string,
    open: PropTypes.func, 
    onClose: PropTypes.func
};
